// @ts-nocheck
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Typography, Button, Icon } from '@material-ui/core';
import { useDialog } from '../../../../common/hooks';
import { useAlert } from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { EmptyState, Panel } from '../../../../common/components';
import { selectPendingActions } from '../../../../common/state/pending';
import { selectCustomerDetails, selectRegions } from '../../state/customerSelectors';
import {
  closeCustomer,
  fetchCustomer,
  logoutAllDevices,
  removeBlocks,
  reopenCustomer,
  sendPasswordResetEmail,
  sendVerificationEmail,
  skipMfa,
  updateCustomer,
} from '../../state/customerActions';
import { UserService } from '../../services/UserService';
import CustomerIdentityDetails from './partials/CustomerIdentityDetails';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    maxHeight: '350px',
  },
}));

function CustomerIdentityPage() {
  const [t] = useTranslation();
  const { userId } = useParams();
  const { showAlert } = useAlert();
  const classes = useStyles();
  const pendingActions = useSelector(selectPendingActions);
  const customerDetails = useSelector(selectCustomerDetails);
  const regions = useSelector(selectRegions);
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [closed, setClosed] = useState(false);
  const [reopened, setReopened] = useState(false);

  const handleSaveCustomer = useCallback(
    async userData => {
      try {
        await dispatch(updateCustomer(userId, userData));
        await dispatch(fetchCustomer(userId));
        showAlert({
          title: t('common.success'),
          message: t('customer.customerSaveSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('customer.customerSaveError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, t, userId]
  );

  const handleSendVerificationEmail = useCallback(async () => {
    try {
      await dispatch(sendVerificationEmail(userId));
      showAlert({
        title: t('common.success'),
        message: t('customer.customerSendVerificationEmailSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('customer.customerSendVerificationEmailError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, userId]);

  const { mutateAsync: handleResetPassword } = useMutation(
    () => {
      return UserService.resetPassword(userId);
    },
    {
      onError: (e: Error) => {
        showAlert({
          title: t('customer.customerResetPasswordError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleSendResetPasswordEmail = useCallback(async () => {
    try {
      await dispatch(sendPasswordResetEmail(userId));
      return () => {
        showDialog({
          content: ({ onCancel }) => {
            return (
              <Box
                className={classes.dialogContent}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography>
                  <b>{t('customer.customerSendResetPasswordEmailSuccess')}</b>
                </Typography>
                <Button
                  id="CustomerIdentityPage-customerSendResetPasswordEmailSuccessButton"
                  color="primary"
                  startIcon={<Icon>task_alt</Icon>}
                  onClick={onCancel}
                >
                  <Typography variant="button" noWrap>
                    OK
                  </Typography>
                </Button>
              </Box>
            );
          },
          hideFooter: true,
        });
      };
    } catch (e) {
      showAlert({
        title: t('customer.customerSendResetPasswordEmailError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [classes.dialogContent, dispatch, showAlert, showDialog, t, userId]);

  const handleLogoutAllDevices = useCallback(async () => {
    try {
      await dispatch(logoutAllDevices(userId));
      showAlert({
        title: t('common.success'),
        message: t('customer.customerLogoutAllDevicesSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('customer.customerLogoutAllDevicesError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, userId]);

  const handleCloseCustomer = useCallback(async () => {
    try {
      await dispatch(closeCustomer(userId));
      await dispatch(fetchCustomer(userId));
      setClosed(true);
      setReopened(false);
      showAlert({
        title: t('common.success'),
        message: t('customer.customerCloseSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('customer.customerCloseError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, userId]);

  const handleReopenCustomer = useCallback(async () => {
    try {
      await dispatch(reopenCustomer(userId));
      await dispatch(fetchCustomer(userId));
      setReopened(true);
      setClosed(false);
      showAlert({
        title: t('common.success'),
        message: t('customer.customerReopenSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('customer.customerReopenError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, userId]);

  const handleRemoveBlocks = useCallback(async () => {
    try {
      await dispatch(removeBlocks(userId));
      await dispatch(fetchCustomer(userId));
      showAlert({
        title: t('common.success'),
        message: t('customer.customerRemoveBlocksSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('customer.customerRemoveBlocksError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, userId]);

  const handleSkipMfa = useCallback(async () => {
    try {
      await dispatch(skipMfa(userId));
      showAlert({
        title: t('common.success'),
        message: t('customer.customerSkipMfaSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('customer.customerSkipMfaError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t, userId]);

  return (
    <Box id="CustomerIdentity-container" className={classes.container}>
      <Panel id="CustomerIdentity-panel" variant="contained">
        {!customerDetails ? (
          <EmptyState variant="unavailable" message={t('customer.customerDetailsNotAvailable')} />
        ) : (
          <CustomerIdentityDetails
            customer={customerDetails}
            regions={_.defaultTo(regions, [])}
            onSaveCustomer={handleSaveCustomer}
            onSendVerificationEmail={handleSendVerificationEmail}
            onSendResetPasswordEmail={handleSendResetPasswordEmail}
            onResetPassword={handleResetPassword}
            onLogoutAllDevices={handleLogoutAllDevices}
            onCloseCustomer={handleCloseCustomer}
            onReopenCustomer={handleReopenCustomer}
            onRemoveBlocks={handleRemoveBlocks}
            onSkipMfa={handleSkipMfa}
            actionsDisabled={!_.isEmpty(pendingActions)}
            closed={closed}
            reopened={reopened}
          />
        )}
      </Panel>
    </Box>
  );
}

export default CustomerIdentityPage;
